import {
  KEYCLOAK_AUTH_COOKIE_NAME,
  OIDC_SERVICE_WORKER_COOKIE_NAME,
  OIDC_SERVICE_WORKER_ENV_VAR_NAME,
  getOidcServiceWorkerConfig,
  resolveBooleanFlag,
  resolveOidcAuthority,
  resolveOidcScope,
  unregisterOidcServiceWorker,
} from '@metaforcelabs/docgen-identity-web'

const maxLogicFileSizeMb = Number.parseInt(window._env_.REACT_APP_MAX_LOGIC_FILE_SIZE_MB, 10);

// Cookie name must match AuthProxy KeycloakProxyCookieMiddleware.CookieName.
// When set, AuthProxy routes identity-host OIDC calls to Keycloak instead of Identity
// Server; the authority below must be switched to match so the id token's issuer validates.
const useKeycloak = resolveBooleanFlag(KEYCLOAK_AUTH_COOKIE_NAME, window._env_.REACT_APP_USE_KEYCLOAK)

export const config = {
  tokenStorageKey: '@MetaForce:userToken',
  apiBaseUrl: window._env_.REACT_APP_API_BASE_URL,
  identityBaseUrl: window._env_.REACT_APP_IDENTITY_BASE_URL,
  centerpointBaseUrl: window._env_.REACT_APP_CENTERPOINT_BASE_URL,
  centerpointUiBaseUrl: window._env_.REACT_APP_CENTERPOINT_UI_BASE_URL,
  // InteractAdmin's own product (IA), the licence its API enforces. This held Support's id before.
  productLicense: '6422d5af5a122315b218c25e',
  appBaseUrl: window._env_.REACT_APP_REDIRECT_URI,
  postLogoutRedirectUri: window._env_.REACT_APP_POST_LOGOUT_REDIRECT_URI,
  docsUrl: window._env_.REACT_APP_DOCUMENTATION_URL,
  maxLogicFileSizeMb: Number.isFinite(maxLogicFileSizeMb) ? maxLogicFileSizeMb : 100,
  // Change Company (screens/SelectCompany) branches on this: an app-hosted picker under
  // Keycloak, the old IdentityServer-hosted /customerresolve page otherwise.
  useKeycloak,
};

// CEN-322. Off by default everywhere; flip per-browser with the use_oidc_service_worker cookie
// (mirrors the Keycloak cookie above) or per-environment with REACT_APP_USE_OIDC_SERVICE_WORKER
// (deployment/values.yaml features.oidcServiceWorkerEnabled) - no redeploy of anything else
// needed either way. Falling back to today's plain (non-worker) mode when off. Ported from
// centerpoint's own CEN-322 rollout (centerpoint#285/#346/#347) once that flag was proven live
// on centerpoint's stage - see ~/Documents/doxis-working-plans/docgen-auth-plan/.
const useOidcServiceWorker = resolveBooleanFlag(
  OIDC_SERVICE_WORKER_COOKIE_NAME,
  window._env_[OIDC_SERVICE_WORKER_ENV_VAR_NAME],
  true, // allowCookieOff: cookie "0"/"false" forces the mode off for this browser
)

// A no-op while useOidcServiceWorker is true; otherwise unregisters the OIDC worker so a
// browser that just turned the mode off is not left signed-in-looking but controlled by a
// worker with no tokens for it - see unregisterOidcServiceWorker's own doc comment.
unregisterOidcServiceWorker(useOidcServiceWorker)

// The realm name, the authority construction and the offline_access rule all come from the
// shared package (MET2-468). They are properties of the realm and of whichever provider is
// answering, not of this app - thirteen frontends held identical copies, realm literal included.
// What stays here is this app's own scope list.
const authority = resolveOidcAuthority(window._env_.REACT_APP_AUTHORITY, useKeycloak)
const scope = resolveOidcScope('openid profile email api.internal', useKeycloak)

export const oidcConfig = {
  client_id: 'interactadmin_web',
  redirect_uri: window._env_.REACT_APP_REDIRECT_URI,
  response_type: 'code',
  post_logout_redirect_uri: window._env_.REACT_APP_POST_LOGOUT_REDIRECT_URI,
  scope,
  authority,
  silent_redirect_uri: window._env_.REACT_APP_SILENT_REDIRECT_URI,
  automaticSilentRenew: true,
  loadUserInfo: true,
  // sessionStorage (the library default) is per-tab and keeps @axa-fr/oidc-client off its
  // navigator.locks-guarded renewal path - Keycloak's own events show this app signing users in
  // but never doing a REFRESH_TOKEN, unlike centerpoint_web/dialog_web/workflow_web, which all
  // set this the same way. Matches CenterPoint/React/src/utils/config.js's oidcConfig.
  storage: window.localStorage,
  // One shared worker holds tokens for every tab instead of each tab racing the same
  // storage key. Trusted domains: public/OidcTrustedDomains.js. Flag-gated, see above.
  ...getOidcServiceWorkerConfig(useOidcServiceWorker),
};